// Client wrapper over the identify-meal-ingredients edge function.
// Compresses a phone photo of raw ingredients on a counter / cutting
// board, sends it to Claude Haiku with the canonical roster, returns
// the identified ingredient list ready to seed AIRecipe.
//
// Distinct from scan-shelf — that's tuned for fridge/pantry inventory
// (counts, fill estimates, brands). This is "what raw food do you see"
// — used by the snap-a-photo recipe pipeline where the user is showing
// the app what they want to cook with.

import { supabase } from "./supabase";
import { compressImage } from "./compressImage";
import { buildCanonicalRoster } from "./canonicalRoster";

/**
 * @param {object} args
 * @param {File|Blob|string} args.input — phone photo (File from input,
 *   Blob from camera, or already-base64 string)
 * @param {"raw_ingredients"|"finished_dish"} [args.mode] — what kind of
 *   photo this is. `raw_ingredients` (default) returns just an
 *   identified-ingredient list. `finished_dish` returns a richer
 *   reverse-engineered sketch: dishName + structure + techniques +
 *   finishing + plating + ingredients (visible + likely).
 * @returns {Promise<
 *   Array<{canonicalId, name, emoji, state, confidence}>
 *   |
 *   {
 *     mode: "finished_dish",
 *     ingredients: Array<...>,
 *     dishName: string|null,
 *     structure: object|null,
 *     techniques: string[],
 *     finishing: object|null,
 *     plating: string|null,
 *     confidence: "high"|"medium"|"low",
 *   }
 * >}
 *   raw_ingredients mode → returns the bare ingredient array (legacy
 *   shape for backwards compat with existing callers).
 *   finished_dish mode → returns the full sketch object.
 * @throws when the edge function errors or returns a malformed payload
 */
export async function identifyMealIngredients({
  input,
  mode = "raw_ingredients",
  description = null,
} = {}) {
  if (!input) throw new Error("a photo is required");

  // 1400px @ q0.85 — bumped from 800@0.70 because Sonnet was
  // missing legible spice-jar labels. At 800px, a small jar in the
  // corner of a frame ends up ~150px wide and JPEG artifacts smear
  // letterforms enough that "OREGANO" stops being unambiguously
  // legible. At 1400@0.85 the same jar's label clears the OCR-
  // legibility threshold without bloating the request meaningfully
  // (~600 KB typical, vs ~200 KB before — well under the
  // egress-discipline ceiling for a user-action-fired feature).
  // Mirrors `scan-nutrition-label`, which faces the same fine-print
  // OCR challenge.
  const { base64, mediaType } = await compressImage(input, {
    maxDimension: 1400,
    jpegQuality: 0.85,
  });

  if (!base64) throw new Error("couldn't read that photo — try again");

  // Ship the canonical roster so Haiku binds emitted names → known ids
  // at identify-time. Same payload generate-recipe receives.
  const roster = buildCanonicalRoster().map((r) => ({
    id: r.id,
    name: r.name,
    category: r.category || "other",
  }));

  const { data, error } = await supabase.functions.invoke(
    "identify-meal-ingredients",
    {
      body: {
        image: base64,
        mediaType,
        ingredients: roster,
        mode,
        ...(description ? { description } : null),
      },
    },
  );

  if (error) {
    let detail = "";
    const ctx = error.context;
    if (ctx && typeof ctx.text === "function") {
      try { detail = await ctx.text(); } catch { /* swallow */ }
    }
    let parsedDetail = "";
    if (detail) {
      try {
        const j = JSON.parse(detail);
        parsedDetail = j?.detail || j?.error || detail;
      } catch { parsedDetail = detail; }
    }
    throw new Error(
      parsedDetail
        ? `Couldn't read the photo: ${String(parsedDetail).slice(0, 300)}`
        : `Couldn't read the photo: ${error.message || "unknown error"}`,
    );
  }

  if (!data || !Array.isArray(data.ingredients)) {
    throw new Error("Couldn't read the photo: empty response");
  }

  // raw_ingredients mode keeps the legacy bare-array return so the
  // existing call sites (PhotoStage's onIdentify) don't need to change.
  // finished_dish mode returns the full sketch object — callers that
  // request it must handle the new shape.
  if (mode === "finished_dish") {
    const result = {
      mode: "finished_dish",
      ingredients: data.ingredients,
      dishName:   typeof data.dishName  === "string" ? data.dishName  : null,
      structure:  data.structure  && typeof data.structure  === "object" ? data.structure  : null,
      techniques: Array.isArray(data.techniques) ? data.techniques.filter((t) => typeof t === "string") : [],
      sauces:     Array.isArray(data.sauces) ? data.sauces : [],
      finishing:  data.finishing  && typeof data.finishing  === "object" ? data.finishing  : null,
      plating:    typeof data.plating === "string" ? data.plating : null,
      confidence: data.confidence === "high" || data.confidence === "low"
        ? data.confidence
        : "medium",
      followUpQuestions: Array.isArray(data.followUpQuestions) ? data.followUpQuestions : [],
    };
    // eslint-disable-next-line no-console
    console.info("[identify] finished_dish →", {
      dishName:    result.dishName,
      ingredients: result.ingredients.map((i) => ({ canonicalId: i.canonicalId, name: i.name })),
      techniques:  result.techniques,
      sauces:      result.sauces.map((s) => s.name),
      finishing:   result.finishing?.type || null,
      followUps:   result.followUpQuestions.length,
    });
    return result;
  }
  // eslint-disable-next-line no-console
  console.info("[identify] raw_ingredients →",
    data.ingredients.map((i) => ({ canonicalId: i.canonicalId, name: i.name, state: i.state })),
  );
  return data.ingredients;
}
